/**
 * Converts certain string fields in a programme object to numbers or null.
 * - Converts `programme__ects` and `programme__eqf_level_provided` to integers.
 * - Converts empty strings to `null`.
 * 
 * @param {Object} obj - Programme object with string values.
 * @returns {Object} A new object with converted values.
 */
export function convertProgrammeStringToNumbers(obj) {
  const result = {}

  for (const key in obj) {
    if (key === 'programme__ects' || key === 'programme__eqf_level_provided') {
      result[key] = parseInt(obj[key], 10)
    } else if (obj[key] === "") {
      result[key] = null
    } else {
      result[key] = obj[key]
    }
  }

  if (result.programme__length === "") {
    result.programme__length = null
  }

  return result
}

/**
 * Converts certain fields in a course object to numbers or string.
 * - Converts `course__ects` to an integer.
 * - Keeps non-empty values as-is.
 * 
 * @param {Object} obj - Course object with string values.
 * @returns {Object} A new object with converted values.
 */
export function convertCourseStringToNumbers(obj) {
  const result = {};

  for (const key in obj) {
    if (obj[key]) {
      if (key === "course__ects") {
        result[key] = parseInt(obj[key], 10);
      } else if (
        key === "course__restricted_to_local" ||
        key === "course__restricted_to_alliance"
      ) {
        result[key] = String(obj[key]);
      } else if (key === "course__related_programme" && Array.isArray(obj[key])) {

        result[key] = obj[key].map((item) => ({
          ...item,
          mandatory: item.mandatory != null ? String(item.mandatory) : item.mandatory,
        }))

      } else {
        result[key] = obj[key]
      }
    }
  }

  return result
}

/**
 * Performs a deep equality check between two values.
 * - Treats `null` and `""` as equal.
 * - Treats `null` and `[]` as equal.
 * - Recursively compares objects and arrays.
 * 
 * @param {*} obj1 - First value to compare.
 * @param {*} obj2 - Second value to compare.
 * @returns {boolean} True if deeply equal, otherwise false.
 */
export function isDeepEqual(obj1, obj2) {
  // Treat null and "" as equal
  if ((obj1 === null && obj2 === "") || (obj1 === "" && obj2 === null)) {
    return true
  }

  // Treat null and [] as equal
  if (
    (obj1 === null && Array.isArray(obj2) && obj2.length === 0) ||
    (obj2 === null && Array.isArray(obj1) && obj1.length === 0)
  ) {
    return true
  }

  // Strict equality for primitives and same reference
  if (obj1 === obj2) return true

  // If either is not an object (primitive mismatch), not equal
  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Different number of keys
  if (keys1.length !== keys2.length) return false

  // Check each key's value
  for (let key of keys1) {
    if (!keys2.includes(key) || !isDeepEqual(obj1[key], obj2[key])) {
      return false
    }
  }

  return true
}

/**
 * Creates a diff object representing changes between two objects.
 * - Compares each key using deep equality.
 * - Includes only changed fields.
 * - Always preserves `id`.
 * 
 * @param {Object} original - Original object.
 * @param {Object} updated - Updated object.
 * @returns {Object} A patch object with only changed keys.
 */
export function getPatchDiff(original, updated) {
  const diff = { id: updated.id }
  const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)])

  for (const key of allKeys) {
    if (key === "id") continue

    if (!isDeepEqual(original[key], updated[key])) {
      diff[key] = updated[key]
    }
  }

  return diff
}

/**
 * Converts a raw entity key into a human-readable header.
 * - Special cases for course prerequisites and related programmes.
 * - Removes redundant parts like "programme", "course", "elm".
 * - Replaces underscores with spaces.
 * 
 * @param {string} key - Raw entity key.
 * @returns {string} Human-readable header.
 */
export function parseEntityHeader(key) {
  if (key === "course__prerequisite_course") {
    return "prerequisite course(s)"
  }

  if (key === "course__related_programme") {
    return "related programme(s)"
  }

  let newKey = ""
  newKey = key
    .replaceAll("_", " ")
    .replaceAll("__", "")
    .replaceAll("programme", "")
    .replaceAll("course", "")
    .replaceAll("elm", "")
    .toUpperCase()

  return newKey
}

/**
 * Flattens a nested object into a single-level key-value map.
 * Example:
 * {
 *   "": "- Language -",
 *   "EU official languages": { "en": "English" },
 *   "Other languages": { "fr": "French" }
 * }
 * becomes:
 * { "": "- Language -", "en": "English", "fr": "French" }
 *
 * @param {Object} obj - The object to flatten.
 * @returns {Object} Flattened key-value mapping.
 */
export function flattenFieldOptions(obj) {
  const result = {}

  for (const [key, value] of Object.entries(obj)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      // Recursively flatten nested objects
      Object.assign(result, flattenFieldOptions(value))
    } else {
      result[key] = value
    }
  }

  return result
}

/**
 * Parses and formats an entity field value for display.
 * - Returns "Not provided" if value is null or empty.
 * - Maps values using `fieldOptions` if available.
 * - Supports deep cloning of programme data to avoid mutations.
 * 
 * @param {string} key - Entity key.
 * @param {*} value - Entity value.
 * @param {Object} fieldOptions - Mapping of field values to display values.
 * @returns {*} A formatted value or JSX element for display.
 */
export function parseEntityValue(key, value, fieldOptions = {}, id = null) {

  const flatOptions = flattenFieldOptions(fieldOptions)
  
  if (
    value == null ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return <span className="fst-italic">{"Not provided"}</span>
  }

  if (value === "false" || value === false) return "No"
  if (value === "true" || value === true) return "Yes"

  if(key === "url") {
    return (
      <a href={value} target="_blank" rel="noopener noreferrer">
        {value}
      </a>
    )
  }

  if(key === "course__related_programme" && id != null) {
    return (
      <a href={`/cc/programmes/${id}`} target="_blank" rel="noopener noreferrer">
        {value}
      </a>
    )
  }

  if(key === "course__prerequisite_course" && id != null) {
    return (
      <a href={`/cc/courses/${id}`} target="_blank" rel="noopener noreferrer">
        {value}
      </a>
    )
  }

  const mappedValue = flatOptions[value]
  if (mappedValue !== undefined) return mappedValue

  return value
}

/**
 * Capitalizes the first letter of a string.
 * - Returns an empty string if the input is falsy (`null`, `undefined`, or `""`).
 * 
 * @param {string} str - The string to capitalize.
 * @returns {string} The string with the first letter capitalized.
 * 
 */
export function capitalizeFirstLetter(str) {
  if (!str) return ""
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Processes a form schema object retrieved from the backend.
 *
 * This utility extracts all fields of type `"select"` from the provided form schema
 * and returns a simplified object mapping field keys to their available select options (`fieldOptionsObj`)
 *
 * @function processForm
 * @param {Object} getForm - The form schema returned from the GraphQL query `getForm`.
 * @returns {Object} .fieldOptionsObj - A key-value map of fields with `type: "select"` and their options.
 * 
 */
export function processForm(getForm) {

  // Extract fields of type "select"
  const fieldOptionsObj = {}
  Object.entries(getForm).forEach(([key, field]) => {
    const type = field?.element?.value?.type || field?.element?.lang?.type

    if (type === "select") {
      fieldOptionsObj[key] =
        field?.element?.value?.options || field?.element?.lang?.options
    }
  })

  return fieldOptionsObj
}


/**
 * Processes a form configuration object to extract table header information.
 *
 * This function scans the given `form` object and identifies which fields
 * should be displayed as table columns. It checks if each field contains
 * a `display.column_display` property set to a truthy value.
 *
 * It then returns two structures:
 * - `headers`: an array of keys that represent the visible table columns.
 * - `columnsHeaders`: an object mapping each header key to a CSS column class
 *   (e.g., `"col-3"` if there are three headers).
 *
 * @param {Object} form - The form configuration object, where each key represents a field name.
 * @returns {{ headers: string[], columnsHeaders: Object<string, string> }}
 *   An object containing:
 *   - `headers`: array of field keys that should appear as table headers.
 *   - `columnsHeaders`: mapping of header keys to corresponding column CSS classes.
 *
 * const { headers, columnsHeaders } = processTableHeader(form)
 * // headers => ["title", "credits"]
 * // columnsHeaders => { title: "col-2", credits: "col-2" }
 * 
 */
export function processTableHeader(form) {

  // Get table headers list from form display
  const formArray = Object.entries(form)
  const headers = []
  formArray.forEach(formItem => {
    if(formItem[1]?.display?.column_display) {
      headers.push(formItem[0])
    }
  })

  // Create columnsPerFieldMap
  const columnsHeaders = headers.reduce((acc, key) => {
    acc[key] = "col-" + headers.length
    return acc
  }, {})

  return {
    headers,
    columnsHeaders
  }

}